import { nextTick } from "vue";
import { useRouter } from "vue-router";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import {
  CreateMemberDocument,
  type CreateMemberMutation,
  type CreateMemberMutationVariables,
  UpdateTmpMemberDocument,
  type UpdateTmpMemberMutation,
  type UpdateTmpMemberMutationVariables,
  type TmpMemberUpdateInput,
} from "@/generated/graphql";
import type { FetchResult } from "@apollo/client/core";
import { routerPath } from "@/common/router";
import type { UseMemberRegisterActionsParams } from "../types";

/**
 * 会員登録画面の登録・確認・完了アクションを扱う composable。
 */
export const useMemberRegisterActions = (params: UseMemberRegisterActionsParams) => {
  const {
    errdialog,
    errMessage,
    overlay,
    confirmPage,
    processFlag,
    displayCompletedDialog,
    createMember,
    memberCreateInput,
    createMemberMutation,
    memberNotesBefore,
    messageMemberNotes,
    house_card_display,
    savedTmpMemberId,
    logInput,
    wordList,
    initialize,
  } = params;

  const router = useRouter();

  /**
   * 会員情報更新
   */
  const updateMember = async (): Promise<void> => {
    if (createMember === null) {
      return;
    }

    //  会員更新 input
    memberCreateInput.customer_code = localStorageGetCustomerCode();
    memberCreateInput.chain_code = localStorageGetChainCode();
    memberCreateInput.address1 = createMember.address1;
    memberCreateInput.address2 = createMember.address2;
    memberCreateInput.address3 = createMember.address3;
    memberCreateInput.name1 = createMember.name1;
    memberCreateInput.name2 = createMember.name2;
    memberCreateInput.name_kana1 = createMember.name_kana1;
    memberCreateInput.name_kana2 = createMember.name_kana2;
    memberCreateInput.notes = createMember.notes;
    memberCreateInput.phone = createMember.phone;
    memberCreateInput.shop_code = localStorageGetShopCode();
    memberCreateInput.zip_code = createMember.zip_code;
    memberCreateInput.house_card =
      house_card_display.value == 1 ? createMember.house_card : "";

    //graphql接続
    const apolloClient = await generateApolloClient();

    const result: FetchResult<CreateMemberMutation> = await apolloClient.mutate<
      CreateMemberMutation,
      CreateMemberMutationVariables
    >({
      mutation: CreateMemberDocument,
      variables: {
        input: memberCreateInput,
        logInput: logInput.value,
      },
    });

    createMemberMutation.value = result.data;
  };

  /**
   * 仮会員情報更新
   */
  const updateTmpMember = async (tmp_member_id: string): Promise<void> => {
    //  仮会員更新 input
    const tmpMemberUpdateInput: TmpMemberUpdateInput = {
      tmp_member_id: tmp_member_id,
    };

    //graphql接続
    const apolloClient = await generateApolloClient();

    await apolloClient.mutate<
      UpdateTmpMemberMutation,
      UpdateTmpMemberMutationVariables
    >({
      mutation: UpdateTmpMemberDocument,
      variables: {
        input: tmpMemberUpdateInput,
        logInput: logInput.value,
      },
    });
  };

  /**
   * 会員登録(確認) @Click
   */
  const onClickUpdate = async (): Promise<void> => {
    //処理フラグ
    processFlag.value = 1;

    // 確認ページ表示フラグ
    confirmPage.value = true;

    // 会員備考のオリジナルを保持し、ダブルクォーテーションを削除
    if (createMember.notes?.includes('"')) {
      memberNotesBefore.value = createMember.notes;
      createMember.notes = createMember.notes.replace(/"/g, "");
      messageMemberNotes.value =
        "(※会員備考のダブルクォーテーションは削除されています)";
    }
  };

  /**
   * 会員更新確認キャンセル @Click
   */
  const onClickConfirmUpdateCancel = async (): Promise<void> => {
    // 会員備考のダブルクォーテーションを戻す。
    if (memberNotesBefore.value != "") {
      createMember.notes = memberNotesBefore.value;
      memberNotesBefore.value = "";
      messageMemberNotes.value = "";
    }
    // 確認ページ表示フラグ
    confirmPage.value = false;
  };

  /**
   * 会員情報を更新する @Click
   */
  const onClickConfirmUpdateOk = async (): Promise<void> => {
    overlay.value = true;
    try {
      // 会員情報を更新する
      await updateMember();

      overlay.value = false;
    } catch (err: unknown) {
      console.error(err);
      overlay.value = false;
      errdialog.value = true;

      if (err instanceof Error) {
        errMessage.value = "会員の編集に失敗しました。";
      } else {
        errMessage.value = "予期せぬエラーが発生しました。";
      }

      return;
    }

    // 仮会員情報を更新する
    // =>本会員登録された日時を登録するため
    if (savedTmpMemberId.value) {
      await updateTmpMember(savedTmpMemberId.value);

      // 更新後、保存していた仮会員IDをクリアする
      savedTmpMemberId.value = "";
    }

    nextTick(async () => {
      overlay.value = false;
      displayCompletedDialog.value = true;
    });
  };

  /**
   * 戻る @Click
   */
  const onClickCompletedGoStart = async (): Promise<void> => {
    //初期化処理
    await initialize();

    // 「オペレータ登録」画面へ遷移
    await nextTick(async () => {
      await router.push({ path: routerPath.memberRegister });
    });
  };

  return {
    onClickUpdate,
    onClickConfirmUpdateCancel,
    onClickConfirmUpdateOk,
    onClickCompletedGoStart,
  };
};
